import { useState, useEffect, ReactNode } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import useIsMountedRef from 'hooks/useIsMountedRef';
import { useTranslator } from 'translation/useTranslator';
import { Button, TextField } from '@nps.today/component-library';

export enum AreYouSureModalType {
  Continue = 'Continue',
  Delete = 'Delete',
  Merge = 'Merge'
}

type UnsavedChangesModalProps = {
  type: keyof typeof AreYouSureModalType;
  display: boolean;
  hideModal: () => void;
  title: string;
  description: string | ReactNode;
  onContinue: (() => void) | (() => Promise<any>);
  useConfirmationSafeguard?: boolean;
};

const AreYouSureModal = ({
  type,
  display,
  hideModal,
  title,
  description,
  onContinue,
  useConfirmationSafeguard
}: UnsavedChangesModalProps) => {
  const translator = useTranslator();
  const [isContinueLoading, setIsContinueLoading] = useState(false);
  const isMountedRef = useIsMountedRef();

  const onClose = () => {
    hideModal();
  };
  const onClickContinue = async () => {
    setIsContinueLoading(true);
    await onContinue();
    if (!isMountedRef.current) return;
    setIsContinueLoading(false);
    hideModal();
  };

  const [disableAction, setDisableAction] = useState(useConfirmationSafeguard);
  const confirmationText = type.toUpperCase();

  useEffect(() => {
    setDisableAction(useConfirmationSafeguard);
  }, [display, useConfirmationSafeguard]);

  const onConfirmationSafeguardChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const enteredConfirmationSafeguard = event.target.value;
    setDisableAction(enteredConfirmationSafeguard !== confirmationText);
  };

  const isDelete = AreYouSureModalType[type] === AreYouSureModalType.Delete;

  const getModalTypeText = () => {
    switch (type) {
      case AreYouSureModalType.Continue:
        return translator.continue();
      case AreYouSureModalType.Delete:
        return translator.delete();
      case AreYouSureModalType.Merge:
        return translator.merge();
      default:
        return '';
    }
  };

  return (
    <Dialog open={display} onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {typeof description === 'string' ? (
          <Typography>{description}</Typography>
        ) : (
          <>{description}</>
        )}
      </DialogContent>
      <DialogActions>
        {useConfirmationSafeguard && (
          <TextField
            dataTestId="are-you-sure-modal-confirmation"
            autoFocus
            style={{ width: '15rem', marginRight: '1rem' }}
            label={translator.typeXToConfirm(confirmationText)}
            variant="standard"
            onChange={onConfirmationSafeguardChange}
          />
        )}
        <Button
          dataTestId="are-you-sure-modal-confirm"
          disabled={disableAction}
          color={isDelete ? 'error' : 'primary'}
          variant={isDelete ? 'outlined' : 'contained'}
          onClick={onClickContinue}
          loading={isContinueLoading}
        >
          {getModalTypeText()}
        </Button>
        <Button
          dataTestId="are-you-sure-modal-cancel"
          color="primary"
          variant={isDelete ? 'contained' : 'outlined'}
          onClick={onClose}
        >
          {translator.cancel()}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AreYouSureModal;
